import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import OpinionFormModal from './OpinionFormModal';

function OpinionPanel({ opinions, onCreate, onUpdate, onDelete, onReorder, saving }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const sorted = (opinions || [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const move = async (index, dir) => {
    const next = sorted.slice();
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    await onReorder(next.map((row, i) => ({ id: row.id, sort_order: i + 1 })));
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Daily Opinion cards use the same authoring shape as Pulse hot takes (question + 2/4 options,
        no correct answer) but stay on Daily Tasks as <code>activity_type=opinion</code> — they are
        not written to the Pulse feed.
      </Alert>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">
          Opinion blocks <Chip size="small" label={sorted.length} sx={{ ml: 1 }} />
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Add opinion
        </Button>
      </Box>

      {sorted.length === 0 ? (
        <Typography color="text.secondary">No opinion blocks for this date.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Reward FE</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>
                  <IconButton size="small" disabled={index === 0 || saving} onClick={() => move(index, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === sorted.length - 1 || saving}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  {row.sort_order ?? index + 1}
                </TableCell>
                <TableCell>{row.title || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status || (row.is_published === false ? 'draft' : 'active')}
                    color={
                      (row.status || (row.is_published === false ? 'draft' : 'active')) === 'draft'
                        ? 'default'
                        : 'success'
                    }
                  />
                </TableCell>
                <TableCell>
                  {Array.isArray(row.options) && row.options.length
                    ? row.options.length
                    : row.total_cards_count ?? row.cards?.length ?? 0}
                </TableCell>
                <TableCell>{row.reward_fe ?? row.fan_energy_reward ?? 0}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditing(row);
                      setModalOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setPendingDelete(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <OpinionFormModal
        open={modalOpen}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditing(null);
          }
        }}
        onSubmit={async (payload) => {
          if (editing?.id) await onUpdate(editing.id, payload);
          else await onCreate(payload);
          setModalOpen(false);
          setEditing(null);
        }}
        opinion={editing}
        saving={saving}
      />

      <Dialog open={!!pendingDelete} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>Delete opinion?</DialogTitle>
        <DialogContent>
          <DialogContentText>Delete “{pendingDelete?.title}”?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={async () => {
              await onDelete(pendingDelete.id);
              setPendingDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OpinionPanel;
